import React, { useEffect, useCallback, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { getFileExtension, getFileType } from 'utils/filesUtils';
import { setAppTitle, getTenantDetails } from 'utils/utils';
import { presignedURLGet } from 'modules/file_manager/services';
import { useActivityLogPreview } from 'hooks/useActivityLogPreview';
import GenericTextFileViewer from './viewers/GenericTextFileViewer';
import GenericDocxViewer from './viewers/GenericDocxViewer';
import GenericPDFViewer from './viewers/GenericPDFViewer';

const GenericFilePreviewModal = ({
  visible,
  onHide,
  file = null,
  title = 'File Preview',
  enableActivityLog = false,
  caseDetails = null,
  onMountUnMount = null,
  onOperationBreak = null,
}) => {
  const [fileWithUrl, setFileWithUrl] = useState(null);
  const [urlError, setUrlError] = useState(null);
  const { addActivity } = useActivityLogPreview();

  // Get presigned URL
  useEffect(() => {
    const getPresignedUrl = async () => {
      if (!file || !visible) {
        setFileWithUrl(null);
        return;
      }
      if (file?.file_path) {
        setUrlError(null);

        try {
          const response = await presignedURLGet(file?.file_path, file?.version_id, false, true);
          const fileWithPresignedUrl = {
            ...file,
            url: response.data?.file_url,
          };
          setFileWithUrl(fileWithPresignedUrl);
        } catch (error) {
          console.error('Error getting presigned URL:', error);
          setUrlError('Failed to load file. Please try again.');
        }
      } else {
        setUrlError('No file path provided');
      }
    };

    getPresignedUrl();
  }, [file, visible]);

  // Set app title when modal is visible
  useEffect(() => {
    if (visible && file) {
      setAppTitle(title, file?.display_name || file?.file_name || '');
    }

    return () => {
      if (visible) {
        setAppTitle(title);
      }
    };
  }, [visible, file, title]);

  useEffect(() => {
    return () => {
      if (onMountUnMount && typeof onMountUnMount === 'function' && caseDetails?.case_id) {
        onMountUnMount('file-preview', 'unmounted', caseDetails.case_id);
      }
      setTimeout(() => {
        typeof onOperationBreak === 'function' && onOperationBreak();
      }, 3000);
    };
  }, []);

  const findType = (item) => {
    return getFileType(getFileExtension(item?.display_name || item?.file_name || ''));
  };

  const closePreviewModal = () => {
    if (onHide) {
      onHide();
    }
  };

  const customHeaderTemplate = (data) => {
    return (
      <div>
        <div className="d-flex justify-content-between">
          <div>
            <i className="icon-window-filled pointer F-size14" onClick={data?.onHide}></i>
          </div>
          <span className="ms-3">{file?.display_name || file?.file_name || title}</span>
          <span></span>
        </div>
      </div>
    );
  };

  // Create activity log if enabled
  const createActivityLog = (item) => {
    if (enableActivityLog) {
      setTimeout(() => {
        const [tenantId] = getTenantDetails();
        const data = {
          event_key: 'Read',
          event_type: 'document',
          event_subtype: 'read_document',
          message: `Viewed Document: ${item?.display_name ? item?.display_name : caseDetails?.display_name}`,
          event_status: 'success',
          pk: item?.pk,
          sk: item?.sk,
          event_items: {
            case_id: item?.case_id ? item.case_id : caseDetails?.case_id ? caseDetails.case_id : '',
            tenant_id: tenantId,
            case_id: item?.case_id ? item.case_id : caseDetails?.case_name ? caseDetails.case_name : '',
            is_lead: item?.is_lead ? item?.is_lead : caseDetails?.is_lead,
          },
        };

        addActivity(data);
      }, 1000);

      // Automated time entry logic
      if (!item?.is_lead && onMountUnMount && typeof onMountUnMount === 'function') {
        onMountUnMount('file-preview', 'mounted', caseDetails?.case_id);
      }
    }
  };

  // Render appropriate viewer based on file type
  const renderViewer = useCallback(() => {
    if (!fileWithUrl) return null;

    const fileType = findType(fileWithUrl);
    const viewerProps = {
      onLoad: () => createActivityLog(fileWithUrl),
      item: fileWithUrl,
      fileUrl: fileWithUrl?.url || fileWithUrl?.file_path,
      fileName: fileWithUrl?.display_name || fileWithUrl?.file_name,
    };

    switch (fileType) {
      case 'text':
        return <GenericTextFileViewer {...viewerProps} />;
      case 'doc':
        return (
          <div style={{ height: '100%', width: '100%' }}>
            <GenericDocxViewer {...viewerProps} />
          </div>
        );
      case 'pdf':
        return (
          <div style={{ height: '100%', width: '100%' }}>
            <GenericPDFViewer {...viewerProps} />
          </div>
        );
      default:
        return (
          <div style={{ height: '100%', width: '100%' }}>
            <GenericDocxViewer {...viewerProps} />
          </div>
        );
    }
  }, [fileWithUrl, createActivityLog]);

  // Render error state
  const renderErrorState = () => (
    <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
      <div className="text-center">
        <i className="pi pi-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
        <p className="mt-3 text-danger">{urlError}</p>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    </div>
  );

  if (!visible || !file) {
    return null;
  }

  return (
    <Dialog
      visible={visible}
      modal
      resizable={false}
      draggable={false}
      className="my-dialog custom-dialog preview-dialog"
      header={customHeaderTemplate}
      onHide={closePreviewModal}
      closable={false}
    >
      <div style={{ display: 'flex', flexDirection: 'column' }}>{urlError ? renderErrorState() : renderViewer()}</div>
    </Dialog>
  );
};

export default GenericFilePreviewModal;
